import React, { Suspense, forwardRef, useRef, useImperativeHandle, useEffect } from "react";
import classNames from "classnames";
import { g as globalContext } from "../global-context.js";
const button = "";
const icon = "";
var BUTTON_VARIATIONS = /* @__PURE__ */ ((BUTTON_VARIATIONS2) => {
  BUTTON_VARIATIONS2["PRIMARY"] = "PRIMARY";
  BUTTON_VARIATIONS2["SOLID_STANDARD"] = "SOLID_STANDARD";
  BUTTON_VARIATIONS2["SUCCESS"] = "SUCCESS";
  BUTTON_VARIATIONS2["SOLID_SUCCESS"] = "SOLID_SUCCESS";
  BUTTON_VARIATIONS2["WARNING"] = "WARNING";
  BUTTON_VARIATIONS2["SOLID_WARNING"] = "SOLID_WARNING";
  BUTTON_VARIATIONS2["DANGER"] = "DANGER";
  BUTTON_VARIATIONS2["SOLID_CRITICAL"] = "SOLID_CRITICAL";
  BUTTON_VARIATIONS2["SECONDARY"] = "SECONDARY";
  BUTTON_VARIATIONS2["OUTLINE_STANDARD"] = "OUTLINE_STANDARD";
  BUTTON_VARIATIONS2["SUCCESS_OUTLINE"] = "SUCCESS_OUTLINE";
  BUTTON_VARIATIONS2["OUTLINE_SUCCESS"] = "OUTLINE_SUCCESS";
  BUTTON_VARIATIONS2["WARNING_OUTLINE"] = "WARNING_OUTLINE";
  BUTTON_VARIATIONS2["OUTLINE_WARNING"] = "OUTLINE_WARNING";
  BUTTON_VARIATIONS2["DANGER_OUTLINE"] = "DANGER_OUTLINE";
  BUTTON_VARIATIONS2["OUTLINE_CRITICAL"] = "OUTLINE_CRITICAL";
  BUTTON_VARIATIONS2["TERTIARY"] = "TERTIARY";
  BUTTON_VARIATIONS2["OUTLINE_NEUTRAL"] = "OUTLINE_NEUTRAL";
  BUTTON_VARIATIONS2["GHOST"] = "GHOST";
  return BUTTON_VARIATIONS2;
})(BUTTON_VARIATIONS || {});
var BUTTON_SIZES = /* @__PURE__ */ ((BUTTON_SIZES2) => {
  BUTTON_SIZES2["SMALL"] = "SMALL";
  BUTTON_SIZES2["MEDIUM"] = "MEDIUM";
  BUTTON_SIZES2["LARGE"] = "LARGE";
  return BUTTON_SIZES2;
})(BUTTON_SIZES || {});
const ComponentName$1 = "Icon";
function IconPlaceholder({ ...rest }) {
  return /* @__PURE__ */ React.createElement("span", { "data-qa": "yoti-icon-placeholder", ...rest });
}
function Icon({ component: IconComponent, className: extraClassName, onClick, title }) {
  const getIconProps = (iconClass) => ({
    className: classNames(iconClass, extraClassName),
    onClick
  });
  return /* @__PURE__ */ React.createElement(Suspense, { fallback: /* @__PURE__ */ React.createElement(IconPlaceholder, { ...getIconProps("yoti-icon-placeholder") }) }, /* @__PURE__ */ React.createElement(
    IconComponent,
    {
      title,
      "data-qa": "icon",
      "aria-hidden": title ? "false" : "true",
      ...getIconProps("yoti-icon")
    }
  ));
}
Icon.displayName = ComponentName$1;
const classes = {
  start: "yoti-button-icon-side--start yds-button-icon-side--start",
  end: "yoti-button-icon-side--end yds-button-icon-side--end"
};
const dataQa = {
  start: "button-icon-side-start",
  end: "button-icon-side-end"
};
function IconSide({ type, icon: icon2 }) {
  const isLegacyIcon = !React.isValidElement(icon2);
  const iconWithLegacyFallback = isLegacyIcon ? /* @__PURE__ */ React.createElement(Icon, { component: icon2 }) : icon2;
  return /* @__PURE__ */ React.createElement(
    "span",
    {
      className: classNames("yoti-button-icon-side yds-button-icon-side", classes[type], {
        "yoti-button-icon-side--has-icon yds-button-icon-side--has-icon": icon2
      }),
      "data-qa": dataQa[type]
    },
    icon2 && iconWithLegacyFallback
  );
}
const ComponentName = "Icon";
const isStartingChildComponentName = (children, componentName) => {
  try {
    const element = React.Children.only(children);
    return element.type.displayName.startsWith(componentName);
  } catch {
    return false;
  }
};
const COMPONENT_NAME = "Button";
const Button = forwardRef(
  ({
    children,
    type,
    variation,
    size,
    disabled,
    fullWidth,
    autoFocus,
    onClick,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    isIconOnly,
    iconStart,
    iconEnd,
    tabIndex,
    id,
    role,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedby,
    "aria-haspopup": ariaHaspopup,
    "aria-controls": ariaControls,
    "aria-expanded": ariaExpanded,
    "data-qa": dataQA
  }, externalRef) => {
    const buttonRef = useRef(null);
    const isIconOnlyInternal = isStartingChildComponentName(children, ComponentName);
    const showIconOnly = isIconOnly === void 0 ? isIconOnlyInternal : isIconOnly;
    useImperativeHandle(externalRef, () => buttonRef.current);
    useEffect(() => {
      if (autoFocus && buttonRef.current) {
        buttonRef.current.focus();
      }
    }, [autoFocus, buttonRef]);
    const containerClasses = classNames("yoti-button yds-button", {
      "yoti-button--has-icon-start yds-button--has-icon-start": iconStart,
      "yoti-button--has-icon-end yds-button--has-icon-end": iconEnd,
      "yds-button--solid-standard": variation === BUTTON_VARIATIONS.SOLID_STANDARD,
      "yoti-button--success yds-button--success": variation === BUTTON_VARIATIONS.SUCCESS,
      "yds-button--solid-success": variation === BUTTON_VARIATIONS.SOLID_SUCCESS,
      "yoti-button--warning yds-button--warning": variation === BUTTON_VARIATIONS.WARNING,
      "yds-button--solid-warning": variation === BUTTON_VARIATIONS.SOLID_WARNING,
      "yoti-button--danger yds-button--danger": variation === BUTTON_VARIATIONS.DANGER,
      "yds-button--solid-critical": variation === BUTTON_VARIATIONS.SOLID_CRITICAL,
      "yoti-button--secondary yds-button--secondary": variation === BUTTON_VARIATIONS.SECONDARY,
      "yds-button--outline-standard": variation === BUTTON_VARIATIONS.OUTLINE_STANDARD,
      "yoti-button--success-outline yds-button--success-outline": variation === BUTTON_VARIATIONS.SUCCESS_OUTLINE,
      "yds-button--outline-success": variation === BUTTON_VARIATIONS.OUTLINE_SUCCESS,
      "yoti-button--warning-outline yds-button--warning-outline": variation === BUTTON_VARIATIONS.WARNING_OUTLINE,
      "yds-button--outline-warning": variation === BUTTON_VARIATIONS.OUTLINE_WARNING,
      "yoti-button--danger-outline yds-button--danger-outline": variation === BUTTON_VARIATIONS.DANGER_OUTLINE,
      "yds-button--outline-critical": variation === BUTTON_VARIATIONS.OUTLINE_CRITICAL,
      "yoti-button--tertiary yds-button--tertiary": variation === BUTTON_VARIATIONS.TERTIARY,
      "yds-button--outline-neutral": variation === BUTTON_VARIATIONS.OUTLINE_NEUTRAL,
      "yoti-button--ghost yds-button--ghost": variation === BUTTON_VARIATIONS.GHOST,
      "yoti-button--small yds-button--small": size === BUTTON_SIZES.SMALL,
      "yoti-button--medium yds-button--medium": size === BUTTON_SIZES.MEDIUM,
      "yoti-button--large yds-button--large": size === BUTTON_SIZES.LARGE,
      "yoti-button--full-width yds-button--full-width": fullWidth === true,
      "yoti-button--is-icon-only yds-button--is-icon-only": showIconOnly
    });
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        ref: buttonRef,
        type,
        disabled,
        autoFocus,
        onClick,
        onKeyDown,
        onMouseEnter,
        onMouseLeave,
        onFocus,
        onBlur,
        tabIndex,
        className: containerClasses,
        id,
        role,
        "aria-label": ariaLabel,
        "aria-describedby": ariaDescribedby,
        "aria-haspopup": ariaHaspopup,
        "aria-controls": ariaControls,
        "aria-expanded": ariaExpanded,
        "data-qa": dataQA
      },
      /* @__PURE__ */ React.createElement(IconSide, { type: "start", icon: iconStart }),
      /* @__PURE__ */ React.createElement("span", { className: "yoti-button__children yds-button__children" }, children),
      /* @__PURE__ */ React.createElement(IconSide, { type: "end", icon: iconEnd })
    );
  }
);
Button.defaultProps = {
  type: "button",
  variation: BUTTON_VARIATIONS.PRIMARY,
  size: BUTTON_SIZES.MEDIUM,
  "data-qa": "button"
};
const ButtonWithProvider = globalContext.connectGlobalPropsContext(Button, "button");
ButtonWithProvider.displayName = COMPONENT_NAME;
const Button$1 = ButtonWithProvider;
export {
  BUTTON_SIZES,
  BUTTON_VARIATIONS,
  Button$1 as Button,
  COMPONENT_NAME,
  Button$1 as default
};
//# sourceMappingURL=index.js.map
